import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { COLLECTIONS } from "../../../services/collections";
import { useTenant } from "../../../hooks/useTenant";
import { addDays, toISODate, getStartOfWeek } from "../../../utils/date";

/**
 * useGradeData
 * Hook para buscar sessões reais do Firebase para a Grade Operacional em TEMPO REAL.
 * Busca sessões por range de datas (semana ou dia) conforme a view.
 * Utiliza o cache local do Firebase, economizando milhares de leituras diárias e
 * permitindo atualização síncrona se 2 pessoas alterarem dados ao mesmo tempo.
 */
export const useGradeData = ({ referenceDate, view }) => {
    const { idTenant, loading: tenantLoading } = useTenant();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Calcula o range de datas baseado na view
    const dateRange = useMemo(() => {
        if (view === "day") {
            const isoDate = toISODate(referenceDate);
            return { startDate: isoDate, endDate: isoDate };
        }

        // Week view: Dom a Sáb
        const weekStart = getStartOfWeek(referenceDate);
        const weekEnd = addDays(weekStart, 6);
        return {
            startDate: toISODate(weekStart),
            endDate: toISODate(weekEnd),
        };
    }, [referenceDate, view]);

    useEffect(() => {
        if (!idTenant || tenantLoading) return;

        setLoading(true);

        const q = query(
            collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS),
            where("sessionDate", ">=", dateRange.startDate),
            where("sessionDate", "<=", dateRange.endDate),
            orderBy("sessionDate", "asc")
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const rawSessions = [];
            snapshot.forEach((docSnap) => {
                const s = docSnap.data();
                rawSessions.push({
                    ...s,
                    id: docSnap.id,
                    idSession: docSnap.id // Compatibilidade com o GradeGrid
                });
            });

            // Enriquecer sessões com waitlistCount das turmas (classes)
            const uniqueClassIds = [...new Set(rawSessions.map(s => s.idClass).filter(Boolean))];
            const waitlistMap = {};

            // Buscar waitlistCount de cada turma (leitura leve, poucas turmas)
            await Promise.all(uniqueClassIds.map(async (classId) => {
                try {
                    const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, classId);
                    const classSnap = await getDoc(classRef);
                    if (classSnap.exists()) {
                        waitlistMap[classId] = Number(classSnap.data().waitlistCount) || 0;
                    }
                } catch { /* silencioso */ }
            }));

            // Injetar waitlistCount nas sessões
            const enrichedSessions = rawSessions.map(s => ({
                ...s,
                waitlistCount: waitlistMap[s.idClass] || 0
            }));

            setSessions(enrichedSessions);
            setLoading(false);
        }, (error) => {
            console.error("[useGradeData] Erro ao ouvir sessões em tempo real:", error);
            toast.error("Erro ao carregar sessões da grade.");
            setLoading(false);
            setSessions([]);
        });

        // Cleanup listener on unmount or date change
        return () => unsubscribe();
    }, [idTenant, tenantLoading, dateRange.startDate, dateRange.endDate]);

    return {
        sessions,
        loading,
        refresh: () => {
            // Com onSnapshot, refresh manual é obsoleto na maior parte do tempo,
            // mas serve como interface caso precise disparar side-effects.
            // Para "forçar" um recarregamento via cache local/remoto não precisa re-query.
        },
    };
};
